import axios from "axios";
import useSWR, { mutate } from "swr";
import useSWRImmutable from "swr/immutable";
import {
    config,
    getAuthCookie,
    removeAuthCookie,
    setAuthCookie,
    API_URL,
    AUTO_LOGIN_COOKIE,
} from "./apiConfig";
import { updateLocation } from "./storeApi";

const userFetcher = (url) => axios.get(url, {}).then((res) => res.data);

const orderFetcher = (url) => axios.get(url, config).then((res) => res.data);

/**
 * Fetches user information via JWT token
 */
export const fetchProfileData = async () => {
    const token = getAuthCookie();
    if (!token) {
        return { ok: false };
    }

    try {
        const user = await axios.get(`${API_URL}/profile`);
        return { ok: true, user: user };
    } catch {
        return { ok: false };
    }
};

/**
 * useSWR information via JWT token
 */
export const useUser = () => {
    const token = getAuthCookie();
    const { data, error, isValidating } = useSWR(
        () => (token ? `${API_URL}/profile` : null),
        userFetcher
    );

    if (error) {
        removeAuthCookie();
    }

    return {
        user: data ? data : null,
        isLoading: !data && isValidating && !error,
    };
};

export const revalidateUser = (user = null) => {
    if (user === null) {
        mutate(`${API_URL}/profile`);
    } else {
        mutate(`${API_URL}/profile`, { data: user }, false);
    }
};

/**
 * If autologin is set use the cookie value to auth
 * delete autologin cookie afterward
 */
export const loginUserWithAutoLoginCookie = async () => {
    const authCookieValue = getAuthCookie(AUTO_LOGIN_COOKIE);
    const hasAutoLoginCookieSet = Boolean(authCookieValue);

    if (!hasAutoLoginCookieSet) {
        return;
    }

    try {
        const delimiter = "-";
        const [customerId, token] = authCookieValue.split(delimiter);

        if (!customerId || !token) {
            throw new Error("Cookie was not set correcly");
        }

        const payload = { id: parseInt(customerId, 10), token };
        const authResponse = await axios.post(`${API_URL}/auth`, payload);

        if (authResponse.status > 400) {
            throw new Error("Unathorized!");
        }

        const { access_token } = authResponse.data;

        setAuthCookie(true, access_token);

        const profileDataResponse = await fetchProfileData();

        if (!profileDataResponse.ok) {
            throw new Error("Profile could not be loaded");
        }

        const { user } = profileDataResponse.data;
        revalidateUser(user);
    } catch (error) {
        // ignore error
        // console.error(error.message);
    }

    // Clear autologin cookie regardless if login succesfull or not
    removeAuthCookie(AUTO_LOGIN_COOKIE);
};

/**
 * Login with user credentials to receive user info & JWT token
 *
 * @param {string} username
 * @param {string} password
 * @param {boolean} staySigned
 * @returns ok: boolean, user: userObj, msg: errorMsg
 */
export const LoginUser = async (username, password, staySigned = false) => {
    const data = {
        email: username,
        password: password,
        stay_logged_in: staySigned,
    };

    try {
        const response = await axios.post(`${API_URL}/auth`, data);
        if (response.status !== 200) {
            return { ok: false };
        }

        const { access_token } = response.data;

        setAuthCookie(staySigned, access_token);
        const profileDataResponse = await fetchProfileData();

        if (!profileDataResponse.ok) {
            return { ok: false, user: undefined };
        }

        return { ok: true, user: profileDataResponse.user };
    } catch (err) {
        return { ok: false, msg: err.response.data.message };
    }
};

/**
 * Retrieves values for { firstName | lastName | email | password } and registers user
 *
 * @param {object} values
 */
export const registerUser = async (values) => {
    try {
        const response = await axios.post(`${API_URL}/auth/register`, values);

        if (response.status !== 200) {
            throw new Error();
        }

        const { email, password } = values;
        const userResponse = await LoginUser(email, password);
        await updateLocation();
        return userResponse;
    } catch (err) {
        return { ok: false, user: null };
    }
};

export const setPassword = async (token, password) => {
    try {
        const response = await axios.post(`${API_URL}/auth/reset`, {
            token,
            password,
        });

        if (response.status >= 400) {
            throw new Error();
        }

        return { ok: true, message: "Password set action was successful" };
    } catch (error) {
        return { ok: false, message: "Error when setting password" };
    }
};

export const resetPassword = async (email) => {
    try {
        const response = await axios.post(`${API_URL}/auth/recover`, {
            email,
        });

        if (response.status >= 400) {
            throw new Error();
        }

        return { ok: true, message: "Password reset action was successful" };
    } catch (error) {
        return { ok: false, message: "Error when reseting password" };
    }
};

export const updatePassword = async (data) => {
    const { password } = data;
    try {
        const response = await axios.patch(`${API_URL}/profile`, {
            password: password,
        });

        if (response.status === 200) {
            return { ok: true };
        }
    } catch (err) {
        return { ok: false };
    }
};

export const updateUser = async (data) => {
    const { name, lastName, email, phone } = data;
    try {
        const response = await axios.patch(`${API_URL}/profile`, {
            first_name: name,
            last_name: lastName,
            email,
            phone,
        });

        if (response.status === 200) {
            return { ok: true, user: response.data };
        }
    } catch (err) {
        return { ok: false };
    }
};

export const logoutUser = async () => {
    try {
        await axios.post(`${API_URL}/auth/logout`);
    } finally {
        removeAuthCookie();
        revalidateUser();
    }
};

export const useUserOrder = (cartUid, requestUid) => {
    if (!requestUid || !cartUid) {
        return {
            order: undefined,
            isLoading: false,
            isError: true,
        };
    }

    const url = `${API_URL}/orders/by-uids?uid=${cartUid}&request_uid=${requestUid}`;

    const { data, error, isValidating } = useSWRImmutable(url, orderFetcher);
    return {
        order: data,
        isLoading: isValidating && !error && !data,
        isError: error,
    };
};

export const useUserOrders = (orderId = undefined) => {
    const token = getAuthCookie();
    const url = orderId ? `${API_URL}/orders/${orderId}` : `${API_URL}/orders`;

    const { data, error, isValidating } = useSWRImmutable(
        token ? url : null,
        orderFetcher
    );

    return {
        orders: data?.items,
        isLoading: isValidating && !error && !data,
        isError: error,
    };
};

export const useUserOngoingOrders = () => {
    const token = getAuthCookie();
    const url = `${API_URL}/orders?current_orders=true`;

    const { data, error, isValidating } = useSWRImmutable(
        token ? url : null,
        orderFetcher
    );

    return {
        orders: data?.items,
        isLoading: isValidating && !error && !data,
        isError: error,
    };
};
